import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { User } from "oidc-client-ts";
import {
  userManager,
  login as oidcLogin,
  logout as oidcLogout,
  autoLoginSSO,
  checkExistingSession,
  getAccessToken,
} from "../auth/oidcConfig";

export interface OidcAuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  refreshAuth: () => Promise<void>;
}

const OidcAuthContext = createContext<OidcAuthState | undefined>(undefined);

export const OidcAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user state on mount
  const loadUser = useCallback(async (forceLoginOnFail = false) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log("🔄 Loading OIDC user state...");

      // ✅ ALWAYS check with OIDC provider first (prevent stale cache)
      //
      // WHY: Don't trust localStorage on page load because:
      // 1. User might have logged out in another app (Home, etc.)
      // 2. OIDC provider session might be terminated
      // 3. Tokens appear valid but session is actually dead
      // 4. Single Logout (SLO) won't work without checking provider
      //
      // By always checking with OIDC provider first, we ensure:
      // ✅ Detect cross-app logouts
      // ✅ Respect OIDC provider session state
      // ✅ Support Single Logout (SLO)
      // ✅ Fresh authentication on every page load
      console.log("🔍 Checking for existing OIDC session with provider...");
      const { user: sessionUser, hasOidcSession } =
        await checkExistingSession();

      if (sessionUser) {
        console.log("✅ OIDC provider confirms active session");
        setUser(sessionUser);
      } else {
        console.log(
          "❌ No active OIDC session (may have logged out elsewhere)"
        );

        // Clean up any stale tokens in localStorage
        await userManager.removeUser();
        setUser(null);

        // ⚡ SMART AUTO-LOGIN: Only redirect if user has OIDC session
        //
        // hasOidcSession = true when error is:
        // - "consent_required": User logged in, needs to grant permissions → SSO (NO creds!)
        // - "login_required": User logged in, needs to authorize app → SSO (NO creds!)
        // - "interaction_required": User logged in, needs interaction → SSO (NO creds!)
        //
        // hasOidcSession = false for other errors (timeout, network, etc.) → Public portal
        if (forceLoginOnFail && hasOidcSession) {
          // Don't auto-login if we're on the callback route (prevents redirect loop)
          const isCallbackRoute =
            window.location.pathname.includes("/Login/Callback");

          if (isCallbackRoute) {
            console.log(
              "⚠️ On callback route - skipping auto-login to prevent loop"
            );
            return;
          }

          console.log("🔄 OIDC session detected - auto-redirecting for SSO...");
          console.log(
            "💡 You'll be logged in automatically (NO credentials needed)!"
          );

          // Small delay to show loading state
          setTimeout(() => {
            // Use autoLoginSSO with prompt=none to prevent login form
            autoLoginSSO();
          }, 500);
        } else if (!hasOidcSession) {
          console.log("ℹ️  No OIDC session - showing public portal");
        }
      }
    } catch (err) {
      console.error("❌ Error loading OIDC user:", err);
      setError(err instanceof Error ? err.message : "Failed to load user");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper to check if token is expired
  const isTokenExpired = (user: User): boolean => {
    if (!user.expires_at) return false;
    const now = Math.floor(Date.now() / 1000);
    return user.expires_at <= now;
  };

  // Login function
  const login = useCallback(async () => {
    try {
      setError(null);
      await oidcLogin();
    } catch (err) {
      console.error("❌ Login error:", err);
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setError(null);
      await oidcLogout();
      setUser(null);
    } catch (err) {
      console.error("❌ Logout error:", err);
      setError(err instanceof Error ? err.message : "Logout failed");
      throw err;
    }
  }, []);

  // Get access token
  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await getAccessToken();
    } catch (err) {
      console.error("❌ Error getting access token:", err);
      return null;
    }
  }, []);

  // Refresh auth state
  const refreshAuth = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  // Listen for user state changes from UserManager
  useEffect(() => {
    const handleUserLoaded = (loadedUser: User) => {
      console.log("✅ User loaded event:", loadedUser.profile?.email);
      setUser(loadedUser);
      setIsLoading(false);
    };

    const handleUserUnloaded = () => {
      console.log("🚪 User unloaded event");
      setUser(null);
    };

    const handleSilentRenewError = (error: Error) => {
      console.error("❌ Silent renew error:", error);
      setError("Session expired. Please log in again.");
      // Clean up localStorage when silent renew fails
      userManager.removeUser();
      setUser(null);
    };

    const handleAccessTokenExpiring = () => {
      console.log("⚠️ Access token expiring soon");
    };

    const handleAccessTokenExpired = () => {
      console.log("❌ Access token expired");
      // Clean up localStorage when token expires
      userManager.removeUser();
      setUser(null);
    };

    const handleUserSignedOut = () => {
      console.log("🚪 User signed out (detected via session monitoring)");
      console.log("💡 This happens when you log out from another app");
      // Clean up localStorage when session ends
      userManager.removeUser();
      setUser(null);
      setError("You have been logged out.");
    };

    // Subscribe to UserManager events
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    // Load user on mount
    //
    // 🔧 AUTHENTICATION STRATEGY:
    // - Silent SSO ALWAYS attempted first (automatic if already authorized)
    // - If silent SSO fails → Show public portal (user clicks "Login" for SSO)
    //
    // WHY NO AUTO-REDIRECT?
    // - Auto-redirect on page load causes OIDC provider to show login form
    // - Manual "Login" button click enables true SSO (NO credentials!)
    // - User-initiated action is more reliable for SSO than automated redirect
    //
    // USER EXPERIENCE:
    // - Already authorized Policy Portal → Auto-login (private portal) ✅
    // - Logged into Home → Public portal → Click "Login" → SSO (NO credentials!) ✅
    // - NOT logged into any app → Public portal → Click "Login" → Enter credentials
    //
    // ERROR DETECTION (for logging/debugging):
    // - "consent_required" / "login_required" / "interaction_required"
    //   → Indicates OIDC session exists (logged into other app)
    // - Other errors → No OIDC session detected
    //
    const forceLoginOnFail = false; // 👈 false = no auto-redirect, show public portal

    loadUser(forceLoginOnFail);

    // Cleanup
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, [loadUser]);

  const value: OidcAuthState = {
    user,
    isAuthenticated: !!user && !isTokenExpired(user),
    isLoading,
    error,
    login,
    logout,
    getToken,
    refreshAuth,
  };

  return (
    <OidcAuthContext.Provider value={value}>
      {children}
    </OidcAuthContext.Provider>
  );
};

// Custom hook to use OIDC auth context
export const useOidcAuth = (): OidcAuthState => {
  const context = useContext(OidcAuthContext);
  if (context === undefined) {
    throw new Error("useOidcAuth must be used within an OidcAuthProvider");
  }
  return context;
};

export default OidcAuthContext;
